import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  doc,
  setDoc,
  getDoc,
  getFirestore,
  collection,
  query,
  where,
  getDocs,
} from "firebase/firestore";
import { ref, uploadBytes, getDownloadURL, getStorage } from "firebase/storage";
import {
  Camera,
  ChevronLeft,
  Eye,
  EyeOff,
  Check,
  X,
  Loader2,
} from "lucide-react";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/material.css";
import { useUserAuth } from "../../context/userAuthContext";

const UserForm = () => {
  const navigate = useNavigate();
  const { user } = useUserAuth();
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ text: "", type: "" });
  const [checkingNickname, setCheckingNickname] = useState(false);

  const [formData, setFormData] = useState({
    nombre: "",
    apellido: "",
    celular: "",
    edad: "",
    sexo: "",
    nickname: "",
    profileImage: null,
    profileImageUrl: "",
  });

  const [errors, setErrors] = useState({
    nickname: "",
  });

  const [nicknameAvailable, setNicknameAvailable] = useState(null);

  useEffect(() => {
    const fetchUserData = async () => {
      if (!user) return;
      try {
        const firestore = getFirestore();
        const docRef = doc(firestore, "Users", user.uid);
        const docSnap = await getDoc(docRef);

        if (docSnap.exists()) {
          const userData = docSnap.data();
          setFormData((prev) => ({
            ...prev,
            ...userData,
            profileImageUrl: userData.photoUrl || "",
          }));
          if (userData.nickname) {
            setNicknameAvailable(true);
          }
        }
      } catch (error) {
        console.error("Error al cargar datos:", error);
        setMessage({
          text: "Error al cargar los datos del usuario",
          type: "error",
        });
      }
    };

    fetchUserData();
  }, [user]);

  const validateNickname = async (nickname) => {
    if (!nickname) {
      setNicknameAvailable(null);
      setErrors((prev) => ({ ...prev, nickname: "" }));
      return false;
    }

    setCheckingNickname(true);
    const nicknameRegex = /^[a-z0-9]+$/;
    if (!nicknameRegex.test(nickname)) {
      setErrors((prev) => ({
        ...prev,
        nickname:
          "Solo minúsculas y números, sin espacios ni caracteres especiales",
      }));
      setNicknameAvailable(false);
      setCheckingNickname(false);
      return false;
    }

    try {
      const firestore = getFirestore();
      const usersRef = collection(firestore, "Users");
      const q = query(usersRef, where("nickname", "==", nickname));
      const querySnapshot = await getDocs(q);

      const isOwnNickname = querySnapshot.docs.some(
        (doc) => doc.id === user.uid
      );

      if (querySnapshot.empty || isOwnNickname) {
        setErrors((prev) => ({ ...prev, nickname: "" }));
        setNicknameAvailable(true);
        setCheckingNickname(false);
        return true;
      } else {
        setErrors((prev) => ({
          ...prev,
          nickname: "Este nickname ya está en uso",
        }));
        setNicknameAvailable(false);
        setCheckingNickname(false);
        return false;
      }
    } catch (error) {
      console.error("Error validando nickname:", error);
      setCheckingNickname(false);
      return false;
    }
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setFormData((prev) => ({
        ...prev,
        profileImage: file,
        profileImageUrl: URL.createObjectURL(file),
      }));
    }
  };

  const uploadImage = async (file) => {
    if (!file) return null;
    try {
      const storage = getStorage();
      const storageRef = ref(storage, `users/${user.uid}/profile.jpg`);
      await uploadBytes(storageRef, file);
      return await getDownloadURL(storageRef);
    } catch (error) {
      console.error("Error al subir imagen:", error);
      throw new Error("Error al subir la imagen de perfil");
    }
  };

  const handleChange = async (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    if (name === "nickname") {
      await validateNickname(value);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!user) {
      setMessage({ text: "No hay usuario autenticado", type: "error" });
      return;
    }

    setLoading(true);

    try {
      const isNicknameValid = await validateNickname(formData.nickname);
      if (!isNicknameValid) {
        setLoading(false);
        return;
      }

      let photoUrl = formData.profileImageUrl;
      if (formData.profileImage) {
        photoUrl = await uploadImage(formData.profileImage);
      }

      const firestore = getFirestore();
      await setDoc(doc(firestore, "Users", user.uid), {
        nombre: formData.nombre,
        apellido: formData.apellido,
        celular: formData.celular,
        edad: formData.edad,
        sexo: formData.sexo,
        nickname: formData.nickname,
        photoUrl: photoUrl,
        uid: user.uid,
        email: user.email,
      });

      setMessage({
        text: "¡Perfil actualizado correctamente!",
        type: "success",
      });
      setTimeout(() => navigate("/profile"), 1500);
    } catch (error) {
      console.error("Error al guardar:", error);
      setMessage({
        text: error.message || "Error al guardar los datos",
        type: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  if (!user) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center p-4">
        <div className="p-6 rounded-2xl w-full max-w-sm">
          <p className="text-red-500 text-center font-medium">
            Por favor, inicia sesión para acceder a esta página.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header - Now more responsive */}
        <div className="sticky top-0 bg-white z-10 py-4 mb-6 flex items-center justify-between border-b">
          <button
            onClick={() => navigate(-1)}
            className="p-2 hover:bg-gray-100 rounded-full transition-colors"
          >
            <ChevronLeft className="w-5 h-5 sm:w-6 sm:h-6" />
          </button>
          <h1 className="text-base sm:text-lg md:text-xl font-semibold">
            Editar Perfil
          </h1>
          <button
            type="button"
            onClick={handleSubmit}
            className="text-blue-500 font-medium p-2 hover:bg-blue-50 rounded-full transition-colors"
          >
            {loading ? (
              <Loader2 className="w-5 h-5 animate-spin" />
            ) : (
              <Check className="w-5 h-5 sm:w-6 sm:h-6" />
            )}
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-8">
          {/* Profile Image - Enhanced responsiveness */}
          <div className="flex justify-center mb-8">
            <div className="relative">
              <div className="w-24 h-24 sm:w-32 sm:h-32 rounded-full overflow-hidden bg-gray-100 border-2 border-gray-200 transition-all duration-300">
                {formData.profileImageUrl ? (
                  <img
                    src={formData.profileImageUrl}
                    alt="Profile"
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <Camera className="w-8 h-8 sm:w-10 sm:h-10 text-gray-400" />
                  </div>
                )}
              </div>
              <label className="absolute bottom-0 right-0 bg-blue-500 rounded-full p-2 sm:p-3 cursor-pointer hover:bg-blue-600 transition-colors">
                <Camera className="w-4 h-4 sm:w-5 sm:h-5 text-white" />
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleImageChange}
                  className="hidden"
                />
              </label>
            </div>
          </div>

          {/* Form Fields - Enhanced grid layout for larger screens */}
          <div className="space-y-6">
            <div className="space-y-4">
              <label className="text-sm text-gray-600 mb-1 block">
                Nombre Completo
              </label>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <input
                  type="text"
                  name="nombre"
                  placeholder="Nombre"
                  value={formData.nombre}
                  onChange={handleChange}
                  required
                  className="p-3 rounded-xl bg-gray-50 border-none focus:ring-2 focus:ring-blue-500 w-full transition-shadow"
                />
                <input
                  type="text"
                  name="apellido"
                  placeholder="Apellido"
                  value={formData.apellido}
                  onChange={handleChange}
                  required
                  className="p-3 rounded-xl bg-gray-50 border-none focus:ring-2 focus:ring-blue-500 w-full transition-shadow"
                />
              </div>
            </div>

            <div>
              <label className="text-sm text-gray-600 mb-1 block">
                Nickname
              </label>
              <div className="relative">
                <input
                  type="text"
                  name="nickname"
                  value={formData.nickname}
                  onChange={handleChange}
                  required
                  placeholder="@nickname"
                  className={`w-full p-3 rounded-xl bg-gray-50 border-none focus:ring-2 focus:ring-blue-500 transition-all ${
                    errors.nickname ? "focus:ring-red-500" : ""
                  }`}
                />
                <div className="absolute right-3 top-1/2 -translate-y-1/2">
                  {checkingNickname ? (
                    <Loader2 className="w-5 h-5 text-gray-400 animate-spin" />
                  ) : nicknameAvailable ? (
                    <Check className="w-5 h-5 text-green-500" />
                  ) : errors.nickname ? (
                    <X className="w-5 h-5 text-red-500" />
                  ) : null}
                </div>
              </div>
              {errors.nickname && (
                <p className="mt-2 text-sm text-red-500">{errors.nickname}</p>
              )}
            </div>

            <div>
              <label className="text-sm text-gray-600 mb-1 block">Email</label>
              <input
                type="email"
                value={user.email}
                disabled
                className="w-full p-3 rounded-xl bg-gray-50 text-gray-500 border-none"
              />
            </div>

            <div>
              <label className="text-sm text-gray-600 mb-1 block">
                Teléfono
              </label>
              <div className="phone-input-container">
                <PhoneInput
                  country={"mx"}
                  value={formData.celular}
                  onChange={(phone) =>
                    setFormData((prev) => ({ ...prev, celular: phone }))
                  }
                  containerClass="w-full"
                  inputClass="w-full p-3 rounded-xl bg-gray-50 border-none focus:ring-2 focus:ring-blue-500"
                  buttonClass="bg-gray-50 border-none rounded-l-xl"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="text-sm text-gray-600 mb-1 block">Edad</label>
                <input
                  type="number"
                  name="edad"
                  value={formData.edad}
                  onChange={handleChange}
                  required
                  min="0"
                  max="120"
                  className="w-full p-3 rounded-xl bg-gray-50 border-none focus:ring-2 focus:ring-blue-500 transition-shadow"
                />
              </div>
              <div>
                <label className="text-sm text-gray-600 mb-1 block">Sexo</label>
                <select
                  name="sexo"
                  value={formData.sexo}
                  onChange={handleChange}
                  required
                  className="w-full p-3 rounded-xl bg-gray-50 border-none focus:ring-2 focus:ring-blue-500 transition-shadow"
                >
                  <option value="">Seleccionar</option>
                  <option value="masculino">Masculino</option>
                  <option value="femenino">Femenino</option>
                  <option value="otro">Otro</option>
                </select>
              </div>
            </div>
          </div>
        </form>

        {/* Toast Message - Now more responsive */}
        {message.text && (
          <div
            className={`fixed bottom-4 left-4 right-4 sm:left-1/2 sm:-translate-x-1/2 sm:max-w-md md:max-w-lg p-4 rounded-xl shadow-lg transition-all duration-300 ${
              message.type === "error"
                ? "bg-red-100 text-red-700"
                : "bg-green-100 text-green-700"
            }`}
          >
            <div className="flex items-center justify-between">
              <span className="text-sm sm:text-base">{message.text}</span>
              <button
                onClick={() => setMessage({ text: "", type: "" })}
                className="ml-4 p-1 hover:bg-white/50 rounded-full transition-colors"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default UserForm;
